// Thư mục: src/stores/xePhanCongStore.js
import { defineStore } from "pinia";
import { XePhanCongApi } from "@/services/xePhanCongService"; // Đổi thành import object

export const useXePhanCongStore = defineStore("xePhanCong", {
  state: () => ({
    loading: false, // Trạng thái loading chung cho các action
    error: null, // Lưu lỗi nếu có

    // Dữ liệu phân công được cache theo xeId
    currentByXe: {}, // Map { xeId: PcxDto | null }
    historyByXe: {}, // Map { xeId: PcxDto[] }

    // Dữ liệu phân công của tài xế đang đăng nhập (cho app tài xế)
    myCurrent: null, // PcxDto | null

    // Tùy chọn chỉ hiển thị tài xế (nếu cần)
    // onlyDrivers: true, // Có thể quản lý ở đây hoặc component
  }),

  actions: {
    // --- Action Tải dữ liệu ---
    async fetchCurrentByXe(xeId) {
      if (!xeId) return; // Bỏ qua nếu xeId rỗng
      this.error = null;
      // Không set loading = true ở đây nếu muốn load nền
      try {
        const data = await XePhanCongApi.getCurrentByXe(xeId); // Gọi service
        // Cập nhật state (dùng $patch cho hiệu quả hoặc gán trực tiếp)
        this.currentByXe = { ...this.currentByXe, [xeId]: data || null };
      } catch (e) {
        console.error(`Lỗi tải phân công hiện tại cho xe ${xeId}:`, e);
        this.currentByXe = { ...this.currentByXe, [xeId]: null }; // Set null nếu lỗi
        // Không set error chung ở đây để tránh ảnh hưởng action khác
      }
    },

    async fetchHistoryByXe(xeId) {
      if (!xeId) return;
      this.error = null;
      // Không set loading = true ở đây nếu muốn load nền
      try {
        const data = await XePhanCongApi.historyByXe(xeId); // Gọi service
        this.historyByXe = {
          ...this.historyByXe,
          [xeId]: Array.isArray(data) ? data : [],
        };
      } catch (e) {
        console.error(`Lỗi tải lịch sử phân công cho xe ${xeId}:`, e);
        this.historyByXe = { ...this.historyByXe, [xeId]: [] }; // Set mảng rỗng nếu lỗi
      }
    },

    // --- Actions Thực hiện Nghiệp vụ ---
    async createAssignment(payload) {
      this.error = null;
      this.loading = true; // Bật loading
      try {
        const data = await XePhanCongApi.createAssignment(payload); // Gọi service
        // Sau khi tạo thành công, tải lại dữ liệu cho xe đó
        if (payload.xeId) {
          // Gọi actions nội bộ để fetch lại
          await Promise.all([
            this.fetchCurrentByXe(payload.xeId),
            this.fetchHistoryByXe(payload.xeId),
          ]);
        }
        return data; // Trả về DTO đã tạo (nếu component cần)
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Lỗi tạo phân công";
        console.error("Lỗi action createAssignment:", e);
        throw e; // Ném lỗi ra để component biết và xử lý (vd: hiển thị alert)
      } finally {
        this.loading = false; // Tắt loading
      }
    },

    async handover(xeId, toTaiXeId, lyDo) {
      this.error = null;
      this.loading = true;
      try {
        const data = await XePhanCongApi.handover(xeId, toTaiXeId, lyDo); // Gọi service
        // Tải lại dữ liệu cho xe đó
        await Promise.all([
          this.fetchCurrentByXe(xeId),
          this.fetchHistoryByXe(xeId),
        ]);
        return data; // Trả về DTO mới (của lệnh TEMP)
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Lỗi bàn giao xe";
        console.error("Lỗi action handover:", e);
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // --- ACTION MỚI: Admin Kết thúc Phân công ---
    async closeAssignment(assignmentId, xeId, lyDo) {
      // Cần cả xeId để refresh
      this.error = null;
      this.loading = true;
      try {
        await XePhanCongApi.closeAssignment(assignmentId, lyDo); // Gọi service
        // Tải lại dữ liệu cho xe liên quan
        if (xeId) {
          await Promise.all([
            this.fetchCurrentByXe(xeId),
            this.fetchHistoryByXe(xeId),
          ]);
        }
        // Không cần trả về data vì API là void
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi kết thúc lệnh giao xe";
        console.error("Lỗi action closeAssignment:", e);
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // --- Action cho Tài xế (Nhận/Trả) ---
    // Component Tài xế sẽ gọi action này
    async postEvent(payload) {
      this.error = null;
      this.loading = true;
      try {
        await XePhanCongApi.postEvent(payload);
        // Lưu ý: Payload chỉ có phanCongId, store không biết xeId nào để refresh.
        // Component sau khi gọi action này thành công NÊN tự gọi lại fetchMyCurrent
        // hoặc truyền thêm xeId vào payload để store refresh.
        // Ví dụ: await this.fetchCurrentByXe(payload.xeId); // Nếu payload có xeId
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Lỗi khi nhận/trả xe";
        console.error("Lỗi action postEvent:", e);
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // --- Action cho Tài xế (Lấy phân công của mình) ---
    // Component Tài xế sẽ gọi action này khi cần
    async fetchMyCurrent() {
      this.error = null;
      this.loading = true;
      this.myCurrent = null; // Reset trước khi fetch
      try {
        const data = await XePhanCongApi.myCurrent(); // Gọi service
        this.myCurrent = data || null;
      } catch (e) {
        // Xử lý lỗi 404 (không có phân công) nhẹ nhàng hơn
        if (e?.response?.status === 404) {
          this.myCurrent = null; // Vẫn là null
        } else {
          this.myCurrent = null;
          this.error =
            e?.response?.data?.message ||
            e?.message ||
            "Lỗi tải thông tin xe hiện tại";
          console.error("Lỗi action fetchMyCurrent:", e);
        }
      } finally {
        this.loading = false;
      }
    },
  },
});
